"use client";

import { useCallback, useMemo } from "react";
import { useParams } from "next/navigation";
import { useShellRouter } from "@/src/hooks";
import { usePathname } from "@/src/hooks/usePathname";
import { locales } from "@utils/i18n";

interface NewsSubTagItem {
  slug: string;
  name: string;
}

/** News 一级频道 + 子标签；与接口下发的 themes 结构一致，仅用于 AI Insight / News 路由 */
export interface NewsThemeGroup {
  channel: string;
  name: string;
  subTags: NewsSubTagItem[];
}

/** 一级 tab 行：手势区只用 key/path，头部 Tab 额外用 label */
export type NewsPrimaryTabRow = {
  key: string;
  path: string;
  label: string;
};

const getNewsPathParams = (pathname: string) => {
  const segments = pathname.split("/").filter(Boolean);
  const localeInPath = locales.includes((segments[0] || "") as any);
  const startIndex = localeInPath ? 1 : 0;

  const isNewsPath = segments[startIndex] === "news";
  const category = isNewsPath ? segments[startIndex + 1] || "" : "";
  const subTag = isNewsPath ? segments[startIndex + 2] || "" : "";

  return { category, subTag };
};

type UseNewsPrimaryTabsParams = {
  groups: NewsThemeGroup[];
};

/**
 * News 一级 tab：locale、路径、tab 列表、当前项与 replace 导航。
 * 仅给 AI Insight 业务组件使用，避免与全局 store 耦合。
 */
export function useNewsPrimaryTabs({ groups }: UseNewsPrimaryTabsParams) {
  const pathname = usePathname(true);
  const params = useParams<{ locale?: string }>();
  const router = useShellRouter();

  const locale = useMemo(() => {
    const segments = pathname.split("/").filter(Boolean);
    const firstSegment = segments[0] || "";
    if (locales.includes(firstSegment as any)) {
      return firstSegment;
    }
    return params.locale || "en";
  }, [pathname, params.locale]);

  const pathPrefix = useMemo(
    () => (locale === "en" ? "" : `/${locale}`),
    [locale],
  );

  const tabs = useMemo<NewsPrimaryTabRow[]>(() => {
    return groups
      .filter((group) => group.subTags.length > 0)
      .map((group) => ({
        key: group.channel,
        label: group.name,
        path: `${pathPrefix}/news/${group.channel}/${group.subTags[0]?.slug || ""}`,
      }))
      .filter((item) => !!item.path.split("/").pop());
  }, [groups, pathPrefix]);

  const { category: currentCategory, subTag: currentSubTag } = useMemo(
    () => getNewsPathParams(pathname || ""),
    [pathname],
  );

  const activePrimaryTabKey = currentCategory;

  const onPrimaryTabChange = useCallback(
    (nextTabKey: string) => {
      const nextTab = tabs.find((item) => item.key === nextTabKey);
      if (!nextTab) {
        return;
      }
      router.replace(nextTab.path);
    },
    [router, tabs],
  );

  return {
    tabs,
    pathPrefix,
    pathname,
    currentCategory,
    currentSubTag,
    activePrimaryTabKey,
    onPrimaryTabChange,
  };
}
